import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  FORGOT_PASSWORD,
  HOMEPAGE,
  LATVIJA_LV_AUTH,
  REGISTER,
} from '../../../router/routes';
import { useService } from '../../../services/services';
import { RootState } from '../../../store/store';
import { guard, user } from '../../../store/global-slice';
import GuestLayout from '../../../components/external/guest-layout';
import AuthButton from '../../../components/external/auth-button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-solid-svg-icons';
import latviaLvLogo from './latvia.lv.logo.svg';
import authCss from '../auth-forms.module.css';
import css from './index.module.css';
import Spinner from '../../../components/common/spinner';
import { concatMap } from 'rxjs/operators';
import { useQuery } from '../../../hooks/use-query';
import GlobalModal from '../../../components/modals/modal';
import { useMessageModal } from '../../../hooks/use-message-modal';
import { SystemConfiguration } from '../../../enums/system-configuration';

export default function Login() {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.external.login.'.concat(key)),
    [t]
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [customError, setCustomError] = useState(null);
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const { register, handleSubmit, formState } = useForm({
    mode: 'onChange',
    defaultValues: { email: '', password: '' },
  });
  const { isValid } = formState;
  const { authService } = useService();
  const messageModal = useMessageModal();
  const { maintenanceMode } = useSelector((state: RootState) => ({
    maintenanceMode: state.global.maintenanceMode,
  }));

  const [params] = useQuery();
  const [registeredModal, setRegisteredModal] = useState(null);
  const onModalClose = useCallback(
    () => setRegisteredModal(null),
    [setRegisteredModal]
  );

  // Reset theme to default
  const bodyRef = document.querySelector('body');
  bodyRef.classList.forEach((className) => {
    className.endsWith('-theme') && bodyRef.classList.remove(className);
  });
  bodyRef.classList.add('default-theme');

  const { tokenValidityHours } = useSelector((state: RootState) => ({
    tokenValidityHours:
      state.global.systemConfiguration[
        SystemConfiguration.VERIFICATION_TOKEN_VALIDITY_HOURS
      ],
  }));

  useEffect(() => {
    if (maintenanceMode) {
      history.push({ pathname: HOMEPAGE });
    }
  }, [maintenanceMode, history]);

  useEffect(() => {
    if (params.registered) {
      setRegisteredModal(
        <GlobalModal
          visible={true}
          shouldCloseOnOverlayClick={false}
          onClose={null}
          buttons={null}
          body={
            <div className={css['registered-modal']}>
              {params.verified === 'true' ? (
                <h1 className={css['registered-title']}>
                  {tp('registered.title-verified')}
                </h1>
              ) : (
                <div>
                  <div className={css['registered-icon']}>
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <h1 className={css['registered-title']}>
                    {tp('registered.title')}
                  </h1>
                  <p className={css['registered-message']}>
                    <Trans
                      i18nKey="pages.external.login.registered.message"
                      components={{ 1: <b />, 2: <br /> }}
                    />
                  </p>
                  <span className={css['registered-expires']}>
                    {tp('registered.expires').concat(
                      ' ',
                      tokenValidityHours,
                      ' ',
                      tp('registered.hours')
                    )}
                  </span>
                </div>
              )}
              <div className={css['registered-btn-container']}>
                <AuthButton onClick={() => onModalClose()}>
                  {tp('registered.agree')}
                </AuthButton>
              </div>
            </div>
          }
        />
      );

      // Remove ?registered=true
      const search = new URLSearchParams(history.location.search);
      search.delete('registered');
      search.delete('verified');
      history.push({ ...history.location, search: search.toString() });
    }
  }, [
    tp,
    history,
    onModalClose,
    params.registered,
    params.verified,
    tokenValidityHours,
  ]);

  const submit = useCallback(
    (data: { email: string; password: string }) => {
      setLoading(true);
      setCustomError(null);

      const $subscription = authService
        .csrf()
        .pipe(
          concatMap(() => authService.login(data.email, data.password)),
          concatMap(() => authService.user())
        )
        .subscribe(
          (response) => {
            dispatch(guard('api'));
            dispatch(user(response));
          },
          ({ response }) => {
            setLoading(false);
            if (response.error) setCustomError(response.error);
            if (response.errors) {
              Object.keys(response.errors).forEach((key) => {
                setCustomError(t(response.errors[key]));
              });
            }
          }
        );

      return () => $subscription.unsubscribe();
    },
    [authService, dispatch, t]
  );

  const goToRegistration = () => {
    history.push({ pathname: REGISTER, search: '?step=options' });
  };

  const loginViaLatviaLv = () => {
    let from = null;

    if (history.location.state && (history.location.state as any).from) {
      from = (history.location.state as any).from;
      from = from?.pathname + from?.search;
    }

    window.location.assign(
      `${LATVIJA_LV_AUTH}?action=login${
        from ? '&from=' + encodeURIComponent(from) : ''
      }`
    );
  };

  const togglePassword = () => setPasswordVisibility(!isPasswordVisible);

  const customErrorMessage = customError && (
    <span className={authCss['error']}>{t(customError)}</span>
  );

  return (
    <GuestLayout>
      <div className={authCss['container']}>
        <form className={authCss['form']} onSubmit={handleSubmit(submit)}>
          <h1>{tp('title')}</h1>
          <p className={authCss['description']}>{tp('description')}</p>
          <div className={authCss['field']}>
            <label htmlFor="email">{tp('email')}:</label>
            <input
              type="text"
              id="email"
              name="email"
              className={authCss['input']}
              autoComplete="username"
              {...register('email')}
            />
          </div>
          <div className={authCss['field']}>
            <label htmlFor="password">{tp('password')}:</label>
            <Link to={FORGOT_PASSWORD} className={css['forgot-link']}>
              {tp('reset-link')}
            </Link>
            <input
              type={isPasswordVisible ? 'text' : 'password'}
              id="password"
              name="password"
              className={authCss['input']}
              autoComplete="current-password"
              {...register('password')}
            />
            <span
              className={authCss['password-switcher']}
              onClick={togglePassword}
            >
              {isPasswordVisible ? (
                <FontAwesomeIcon icon={faEyeSlash} />
              ) : (
                <FontAwesomeIcon icon={faEye} />
              )}
            </span>
          </div>

          {customErrorMessage}

          <div className={authCss['buttons']}>
            <AuthButton
              name="login"
              type="submit"
              disabled={!isValid || isLoading}
            >
              <Spinner visible={isLoading} className={css['spinner']} />
              {tp('title')}
            </AuthButton>
          </div>
          <span className={css['delimiter']}>{tp('delimiter')}</span>

          <div className="mx-3">
            <AuthButton
              name="login-latvijalv"
              theme="light"
              className="w-full"
              onClick={loginViaLatviaLv}
            >
              <span className="leading-8">{tp('sign-in')}</span>
              <img src={latviaLvLogo} alt="" className={css['sign-in-logo']} />
            </AuthButton>
          </div>

          <p className={css['help']}>
            <span>{tp('help.text')}</span>
            <button type="button" onClick={goToRegistration}>
              {tp('help.link')}
            </button>
          </p>
        </form>
      </div>

      {messageModal}
      {registeredModal}
    </GuestLayout>
  );
}
